import { ChevronDown, Download, MessageSquare } from 'lucide-react'
import { useState } from 'react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import {
  redditSearchComments,
  type RedditSearchCommentResult,
} from 'sprites-types/workflow/tools/redditSearchComments'
import { ContextMenuWrapper, type ContextAction } from '../shared/ContextMenuWrapper'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'
import { RedditCommentItem } from './RedditCommentItem'

function RedditSearchCommentsDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(redditSearchComments, nodeExecution.inputs)
  const [isExpanded, setIsExpanded] = useState(true)

  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data

  const comments: RedditSearchCommentResult = Array.isArray(result)
    ? (result as RedditSearchCommentResult)
    : ([] as unknown as RedditSearchCommentResult)

  // Header actions
  const headerActions: ContextAction[] = [
    {
      id: 'export-comments',
      label: 'Export All',
      icon: <Download className="h-4 w-4" />,
      onClick: () => {
        console.log('Export Reddit comments:', comments)
      },
    },
  ]

  if (!comments || comments.length === 0) {
    return (
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <div className="flex items-center gap-4 px-4 py-3">
          <div className="flex-shrink-0">
            <MessageSquare className="h-5 w-5 text-gray-400" />
          </div>
          <div className="text-sm text-gray-600">No comments found</div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {/* Header with context menu and collapse/expand */}
      <ContextMenuWrapper actions={headerActions}>
        <div className="overflow-hidden rounded-3xl border border-gray-200/50 bg-gray-200/70 text-black shadow-sm">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="group flex w-full items-center gap-4 bg-white px-4 py-3 text-left transition-colors hover:bg-gray-50/50"
            aria-expanded={isExpanded}
          >
            <div className="flex-shrink-0">
              <MessageSquare className="h-5 w-5 text-gray-600" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-gray-900">Reddit Comments</div>
              <div className="text-xs font-medium tracking-wide text-gray-500">
                {comments.length} items
              </div>
            </div>
            <div className="flex-shrink-0">
              <ChevronDown
                className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </div>
          </button>
        </div>
      </ContextMenuWrapper>

      {/* Individual comments - show/hide based on expanded state */}
      {isExpanded && (
        <div className="space-y-2">
          {comments.map((comment, index) => (
            <RedditCommentItem key={index} comment={comment} className="mx-8" />
          ))}
        </div>
      )}
    </div>
  )
}

function downloadRedditSearchComments(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function redditSearchCommentsToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(redditSearchComments, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data
  const comments: RedditSearchCommentResult = Array.isArray(result)
    ? (result as RedditSearchCommentResult)
    : ([] as unknown as RedditSearchCommentResult)

  if (!comments || comments.length === 0) {
    return `## Reddit Comments Search

No comments found.

---
`
  }

  const tableHeader = `| Author | Score | Date | Comment |
|--------|-------|------|---------|`

  const tableRows = comments
    .map((comment: any) => {
      const author = `u/${comment.author}`
      const score = `${comment.score} upvotes`
      const createdAt = new Date(comment.created_utc * 1000)
      const dateLabel = isNaN(createdAt.getTime())
        ? String(comment.created_utc)
        : createdAt.toLocaleDateString()
      const body = (comment.body || '').replace(/\|/g, '\\|').replace(/\n/g, ' ').slice(0, 100)
      const bodyDisplay = body.length === 100 ? body + '...' : body

      return `| ${author} | ${score} | ${dateLabel} | ${bodyDisplay} |`
    })
    .join('\n')

  const detailedComments = comments
    .filter((comment: any) => comment.body?.length > 100)
    .slice(0, 10) // Limit to first 10 detailed comments
    .map((comment: any) => {
      const author = `u/${comment.author}`
      const createdAt = new Date(comment.created_utc * 1000)
      const dateLabel = isNaN(createdAt.getTime())
        ? String(comment.created_utc)
        : createdAt.toLocaleDateString()
      return `### ${author} - ${dateLabel}\n\n${comment.body}`
    })
    .join('\n\n')

  return `## Reddit Comments Search (${comments.length} comments)

${tableHeader}
${tableRows}

${detailedComments ? `\n## Detailed Comments\n\n${detailedComments}\n` : ''}
---
`
}

export const RedditSearchCommentsDisplay = createWorkflowToolComponent(
  RedditSearchCommentsDisplayComponent,
  downloadRedditSearchComments,
  redditSearchCommentsToMarkdown
)
